import type { ConditionBuild, DesignerNodeData } from "../../../../schemas/index.ts";
import { traverseNodeSubtree } from "../../../../shared/index.ts";

export const validateIds = (node: DesignerNodeData) => {
  const seenByType = new Map<string, Map<number, string[]>>();

  const record = (locationType: string, id: number, location: string) => {
    let typeMap = seenByType.get(locationType);
    if (!typeMap) {
      typeMap = new Map();
      seenByType.set(locationType, typeMap);
    }
    const locations = typeMap.get(id);
    if (locations) {
      locations.push(location);
    } else {
      typeMap.set(id, [location]);
    }
  };

  traverseNodeSubtree(node, ({ id: nodeId, triggers }: DesignerNodeData) => {
    record("node", nodeId, `node ${nodeId}`);

    if (triggers) {
      for (const { id: triggerId, handlers } of triggers) {
        record("trigger", triggerId, `trigger ${triggerId} in node ${nodeId}`);
        if (handlers) {
          for (const { id: handlerId, responses } of handlers) {
            record("handler", handlerId, `handler ${handlerId} in trigger ${triggerId}`);
            if (responses) {
              for (const { id: responseId, effects, condition: rootCondition } of responses) {
                record("response", responseId, `response ${responseId} in handler ${handlerId}`);
                if (effects) {
                  for (const { id: effectId } of effects) {
                    record("effect", effectId, `effect ${effectId} in response ${responseId}`);
                  }
                }
                if (rootCondition) {
                  const addConditionIds = ({ id: conditionId, children }: ConditionBuild) => {
                    record("condition", conditionId, `condition ${conditionId} in response ${responseId}`);
                    if (children) {
                      for (const condition of Object.values(children)
                        .filter((c): c is NonNullable<typeof c> => !!c)
                        .flat()) {
                        addConditionIds(condition);
                      }
                    }
                  };
                  addConditionIds(rootCondition);
                }
              }
            }
          }
        }
      }
    }
  });

  const duplicates: { id: number; locations: string[] }[] = [];
  for (const typeMap of seenByType.values()) {
    for (const [id, locations] of typeMap) {
      if (locations.length > 1) {
        duplicates.push({ id, locations });
      }
    }
  }

  if (duplicates.length > 0) {
    const details = duplicates
      .map(({ id, locations }) => `id ${id} (${locations.length}x) found in: ${locations.join(", ")}`)
      .join("; ");
    return {
      valid: false,
      reason: `Duplicate ids adding node ${node.id}: ${details}.`,
    } as const;
  }

  return {
    valid: true,
  } as const;
};
